/* eslint-disable */
import {
  Component,
  Directive,
  DoCheck,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  ViewEncapsulation,
  SimpleChanges,
  ChangeDetectorRef,
  Inject,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { _ErrorStateTracker, ErrorStateMatcher } from '@angular/material/core';
import { BooleanInput, coerceBooleanProperty, NumberInput } from '@angular/cdk/coercion';
import { ControlValueAccessor, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { MatFormField, MatFormFieldControl, MAT_FORM_FIELD } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { isNil } from 'lodash-es';

import { Gravatar } from '@startuptools/ui/Gravatar';
import { NgIf } from '@angular/common';
import { ReactWrapperComponent } from '../react-wrapper/react-wrapper.component';
import { MatIcon } from '@angular/material/icon';

export interface ObjectInputForm {
  readonly name: string;
  readonly sub: string;
  readonly gravatarEmail: string | null;
  readonly gravatarHash: string | null;
}

let nextUniqueId = 0;

@Directive()
export abstract class _AppObjectInputBase
  implements OnDestroy, OnInit, DoCheck, ControlValueAccessor, MatFormFieldControl<any>
{
  readonly stateChanges = new Subject<void>();
  private _uid = `app-object-input-${nextUniqueId++}`;

  protected readonly _destroy = new Subject<void>();

  _ariaDescribedby: string;

  _onChange: (value: any) => void = () => null;
  _onTouched = (): void => {};
  _valueId = `app-object-input-value-${nextUniqueId++}`;

  get focused(): boolean {
    return this._focused;
  }
  private _focused = false;

  controlType = 'app-object-input';

  private _errorStateTracker: _ErrorStateTracker;

  @Input()
  get disabled() {
    return false;
  }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    if (this.focused) {
      this._focused = false;
      this.stateChanges.next();
    }
  }
  protected _disabled = false;

  get errorState() {
    return this._errorStateTracker.errorState;
  }
  set errorState(value: boolean) {
    this._errorStateTracker.errorState = value;
  }

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }
  private _placeholder: string;

  /** Whether the component is required. */
  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get value(): any {
    return this._value;
  }
  set value(newValue: any) {
    if (newValue !== this._value) {
      this._value = newValue;
      this._changeDetectorRef.markForCheck();
      this.stateChanges.next();
    }
  }
  private _value: any;

  @Input() errorStateMatcher: ErrorStateMatcher;

  @Output() readonly valueChange: EventEmitter<any> = new EventEmitter<any>();

  @Input()
  get id(): string {
    return this._id;
  }
  set id(value: string) {
    this._id = value || this._uid;
    this.stateChanges.next();
  }
  private _id: string;

  constructor(
    protected _changeDetectorRef: ChangeDetectorRef,
    public elementRef: ElementRef,
    public _defaultErrorStateMatcher: ErrorStateMatcher,
    @Optional() public _parentForm: NgForm,
    @Optional() public _parentFormGroup: FormGroupDirective,
    @Optional()
    @Inject(MAT_FORM_FIELD)
    protected _parentFormField: MatFormField,
    @Self() @Optional() public ngControl: NgControl,
  ) {
    const defaultErrorStateMatcher = inject(ErrorStateMatcher);
    this._errorStateTracker = new _ErrorStateTracker(
      defaultErrorStateMatcher,
      ngControl,
      _parentFormGroup,
      _parentForm,
      this.stateChanges,
    );

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
    this.id = this.id;
  }

  ngOnInit() {
    this.stateChanges.next();
  }

  ngDoCheck() {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes['disabled']) {
      this.stateChanges.next();
    }
  }

  ngOnDestroy() {
    this._destroy.next();
    this._destroy.complete();
    this.stateChanges.complete();
  }

  writeValue(value: any): void {
    this.value = value;
  }

  updateErrorState() {
    this._errorStateTracker.updateErrorState();
  }

  registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._changeDetectorRef.markForCheck();
    this.stateChanges.next();
  }

  focus(options?: FocusOptions): void {
    this.elementRef.nativeElement.focus(options);
  }

  _onFocus() {
    if (!this.disabled) {
      this._focused = true;
      this.stateChanges.next();
    }
  }

  _onBlur() {
    this._focused = false;

    if (!this.disabled) {
      this._onTouched();
      this._changeDetectorRef.markForCheck();
      this.stateChanges.next();
    }
  }

  get empty(): boolean {
    return !this.value;
  }

  setDescribedByIds(ids: string[]) {
    this._ariaDescribedby = ids.join(' ');
  }

  onContainerClick() {
    this.focus();
  }

  get shouldLabelFloat(): boolean {
    return !this.empty;
  }

  static ngAcceptInputType_required: BooleanInput;
  static ngAcceptInputType_disabled: BooleanInput;
  static ngAcceptInputType_tabIndex: NumberInput;
}

@Component({
  selector: 'app-object-input',
  exportAs: 'appObjectInput',
  templateUrl: './object-input.component.html',
  styleUrls: ['./object-input.component.scss'],
  inputs: ['disabled', 'tabIndex'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    // { provide: NG_VALUE_ACCESSOR, useExisting: ObjectInputComponent },
    { provide: MatFormFieldControl, useExisting: ObjectInputComponent },
  ],
  host: {
    '[id]': 'id',
    '[attr.tabindex]': 'tabIndex',
    '[attr.aria-required]': 'required.toString()',
    '[attr.aria-disabled]': 'disabled.toString()',
    '[attr.aria-invalid]': 'errorState',
    '[attr.aria-describedby]': 'describedBy',
    '[class.app-object-input-disabled]': 'disabled',
    '[class.app-object-input-invalid]': 'errorState',
    '[class.app-object-input-required]': 'required',
    '(focus)': '_onFocus()',
    '(blur)': '_onBlur()',
  },
  imports: [NgIf, ReactWrapperComponent, MatIcon],
})
export class ObjectInputComponent extends _AppObjectInputBase {
  Gravatar = Gravatar;
  isNil = isNil;
}
